import axios from 'axios';
import { mergeAuthHeaders, getAccessToken } from '../auth/tokenService';
import { resolveRequestUrl } from './config';
import {
  shouldAttachGlobalApiLoader,
  globalApiLoadingBegin,
  globalApiLoadingEnd,
} from './apiLoading';

let didWarnMissingToken = false;

function buildAbsoluteUrl(path) {
  if (!path.startsWith('/')) return path;
  return resolveRequestUrl(path);
}

export const apiAxios = axios.create({
  timeout: 120000,
  validateStatus: () => true,
});

apiAxios.interceptors.request.use((config) => {
  if (typeof config.url === 'string' && config.url.startsWith('/')) {
    config.baseURL = '';
    config.url = buildAbsoluteUrl(config.url);
  }
  const merged = mergeAuthHeaders({ ...(config.headers || {}) });
  Object.assign(config.headers, merged);
  if (!config.headers['Cache-Control']) {
    config.headers['Cache-Control'] = 'no-cache';
  }
  if (!config.headers.Pragma) {
    config.headers.Pragma = 'no-cache';
  }
  if (config.cache === undefined) {
    config.cache = false;
  }
  try {
    const u = new URL(config.url, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    const cross = typeof window !== 'undefined' && u.origin !== window.location.origin;
    if (cross && config.withCredentials === undefined) {
      config.withCredentials = true;
    }
  } catch {
    /* ignore */
  }
  if (
    process.env.NODE_ENV === 'development' &&
    !didWarnMissingToken &&
    !config.headers.Authorization &&
    typeof config.url === 'string' &&
    (config.url.includes('/api/') || config.url.includes('royalcyber.org'))
  ) {
    didWarnMissingToken = true;
    console.warn(
      '[apiAxios] No access token stored — add ?token=… to the URL once or complete login. Request will have no Authorization header.',
    );
  }
  if (
    typeof config.url === 'string' &&
    shouldAttachGlobalApiLoader(config.url, { skipGlobalLoader: config.skipGlobalLoader })
  ) {
    config.__globalApiLoader = true;
    globalApiLoadingBegin();
  }
  return config;
});

function endGlobalLoaderIfNeeded(config) {
  if (config && config.__globalApiLoader) {
    config.__globalApiLoader = false;
    globalApiLoadingEnd();
  }
}

apiAxios.interceptors.response.use(
  async (response) => {
    const config = response.config;
    endGlobalLoaderIfNeeded(config);

    const ct = (response.headers['content-type'] || '').toLowerCase();
    const wantsBlob = config?.responseType === 'blob' || config?.responseType === 'arraybuffer';
    if (wantsBlob || ct.startsWith('image/')) {
      return response;
    }
    if (ct.includes('text/html')) {
      const hint =
        'The flyer API must be reached as JSON (gateway/proxy should forward /api/templates, /api/flyers, /api/health to Node before any SPA Path=/**).';
      throw new Error(
        `API returned HTML instead of JSON (HTTP ${response.status}). URL: ${response.config?.url || ''}. ${hint}`,
      );
    }
    return response;
  },
  (error) => {
    endGlobalLoaderIfNeeded(error.config);
    return Promise.reject(error);
  },
);

/**
 * Throws with server `error` message if status is not ok (for JSON error bodies).
 */
export function assertAxiosJsonOk(response) {
  if (response.status >= 200 && response.status < 300) return response.data;
  const errBody = response.data && typeof response.data === 'object' ? response.data : {};
  const msg = errBody.error || response.statusText || `HTTP ${response.status}`;
  throw new Error(msg);
}
